import {
  writeContract,
  getAccount,
  getNetwork,
  switchNetwork,
  getWalletClient,
  waitForTransaction,
} from "wagmi/actions";
import { erc20ABI } from "wagmi";
import * as ethers from "ethers";
import Escrow from "@/chain-info/contracts/Escrow.json";
import store from "store2";
import request from "@/utils/request";
import { Provider, L1Signer, utils } from "zksync-web3";
import mitt, { Emitter } from "mitt";
import { walletClientToSigner } from "@/services/ethers";
import { parseEther, parseUnits } from "viem";
import mixpanel from "mixpanel-browser";
import { isSkipError } from "@/utils/validate";

const STORE_TRANSACTIONS = "bridge-transactions";

type PaymentInfo = {
  token: {
    chainId: number;
    tokenAddress: string;
    decimals: number;
  };
  oneclickBridgeId: number;
  escrowContractAddress: string;
  tokenAmount: number;
};

export type OneClickPaymentInfo = PaymentInfo & {
  _serviceId: string;
  _wallet: string;
};

export type zkSyncPaymentInfo = {
  chainId: number;
  tokenAmount: number;
} & {
  _serviceId: string;
  _wallet: string;
};

export type BridgeTask = {
  status: "0" | "2";
  chainId: string;
  createTime: string;
  escrowContractAddress: string;
  fee: string;
  id: number;
  tokenAddress: string;
  tokenAmount: string;
  transactionHash: string | null;
  transactionId: null | string;
  updateTime: string;
  userId: string;
  walletAddress: string;
};

export type ServicePaymentInfo = OneClickPaymentInfo | zkSyncPaymentInfo;

export enum BridgeTransactionStatus {
  PENDING,
  DEPOSIT_SUCCESS,
  DEPOSIT_FAIL,
  MINTING,
  MINT_SUCCESS,
  MINT_FAIL,
}

export type BridgeType = "one-click" | "zkSync";

export type BridgeTransaction = {
  paymentInfo: ServicePaymentInfo;
  transactionHash: string;
  createAt: number;
  status: BridgeTransactionStatus;
  bridgeType: BridgeType;
};

function isNativeToken(address?: string) {
  return (
    address === "0x0000000000000000000000000000000000000000" ||
    address === "0x0000000000000000000000000000000000001010"
  );
}

export class Service {
  id: string;

  constructor(id: string) {
    this.id = id;
  }

  async getPaymentInfo(data: any): Promise<PaymentInfo> {
    throw new Error("Not implemented");
  }
}

type Events = {
  transactionStatusChange: BridgeTransaction;
  newTransaction: BridgeTransaction;
};

export class Bridge {
  serviceId: string;
  type: BridgeType;
  emitter: Emitter<Events>;
  constructor(type: BridgeType, serviceId: string) {
    this.type = type;
    this.serviceId = serviceId;
    this.emitter = mitt();
  }

  newTransaction(transactionHash: string, paymentInfo: ServicePaymentInfo) {
    const transaction: BridgeTransaction = {
      transactionHash,
      paymentInfo,
      createAt: Date.now(),
      status: BridgeTransactionStatus.PENDING,
      bridgeType: this.type,
    };
    store.transact(
      STORE_TRANSACTIONS,
      (trans: BridgeTransaction[] | undefined) => {
        return (trans || []).concat(transaction);
      }
    );
    this.emitter.emit("newTransaction", transaction);
    mixpanel.track("bridge_new_transaction", {
      serviceId: this.serviceId,
      transaction,
    });
  }
  updateTransactionStatus(
    transactionHash: string,
    status: BridgeTransactionStatus
  ) {
    store.transact(
      STORE_TRANSACTIONS,
      (trans: BridgeTransaction[] | undefined) => {
        const transaction = (trans || []).find((transaction) => {
          return transaction.transactionHash === transactionHash;
        });
        if (transaction) {
          transaction.status = status;
        }
      }
    );
    const transaction = this.getTransaction(transactionHash);
    if (transaction) {
      this.emitter.emit("transactionStatusChange", transaction);
      mixpanel.track("bridge_transaction_update", {
        serviceId: this.serviceId,
        transaction,
      });
    }
    console.log("transactionStatusChange", transaction);
  }

  getTransaction(transactionHash: string) {
    const transactions: BridgeTransaction[] =
      store.get(STORE_TRANSACTIONS) || [];
    return transactions.find((trans) => {
      return trans.transactionHash === transactionHash;
    });
  }

  getTransactions(status?: BridgeTransactionStatus) {
    const transactions: BridgeTransaction[] =
      store.get(STORE_TRANSACTIONS) || [];
    return transactions.filter((trans) => {
      if (
        trans.bridgeType !== this.type ||
        trans.paymentInfo._serviceId !== this.serviceId
      ) {
        return false;
      }
      if (typeof status !== "undefined") {
        return trans.status === status;
      }
      return true;
    });
  }

  async getPaymentInfo(wallet: string, data: any): Promise<ServicePaymentInfo> {
    throw new Error("Not implemented");
  }

  async deposit(paymentInfo: ServicePaymentInfo): Promise<BridgeTransaction> {
    throw new Error("Not implemented");
  }

  async waitUntilMintSuccess(
    transaction: BridgeTransaction,
    timeout: number = 60000
  ): Promise<boolean> {
    throw new Error("Not implemented");
  }
}

export class ZKSyncBridge extends Bridge {
  constructor() {
    super("zkSync", "zksync");
  }

  async getPaymentInfo(
    wallet: string,
    data: {
      chainId: number;
      tokenAmount: number;
    }
  ): Promise<zkSyncPaymentInfo> {
    return {
      ...data,
      _wallet: wallet,
      _serviceId: this.serviceId,
    };
  }

  async deposit(paymentInfo: zkSyncPaymentInfo) {
    mixpanel.track("bridge_deposit", {
      serviceId: this.serviceId,
      type: this.type,
      paymentInfo,
    });
    const { chain } = getNetwork();
    if (chain?.id !== paymentInfo.chainId) {
      await switchNetwork({
        chainId: paymentInfo.chainId,
      });
    }

    const zksyncProvider = new Provider(
      paymentInfo.chainId === 1
        ? "https://mainnet.era.zksync.io"
        : "https://testnet.era.zksync.dev"
    );
    const walletClient = await getWalletClient({
      chainId: paymentInfo.chainId,
    });
    const signer = L1Signer.from(
      walletClientToSigner(walletClient),
      zksyncProvider
    );
    const depositHandle = await signer.deposit({
      token: utils.ETH_ADDRESS,
      amount: ethers.utils.parseEther(paymentInfo.tokenAmount.toFixed(18)),
    });

    this.newTransaction(depositHandle.hash, paymentInfo);
    await depositHandle.wait();
    this.updateTransactionStatus(
      depositHandle.hash,
      BridgeTransactionStatus.DEPOSIT_SUCCESS
    );
    return this.getTransaction(depositHandle.hash)!;
  }
}

export class OneClickBridge extends Bridge {
  constructor(serviceId: string) {
    super("one-click", serviceId);
  }

  async getPaymentInfo(
    wallet: string,
    data: any
  ): Promise<OneClickPaymentInfo> {
    const resp = await request.rpc(this.serviceId, data as any);
    return {
      ...resp,
      _wallet: wallet,
      _serviceId: this.serviceId,
    } as any;
  }

  async oneClickBridgeConfirm(transaction: BridgeTransaction) {
    if (transaction.bridgeType !== "one-click") {
      throw new Error("Transaction type is not one-click");
    }
    const paymentInfo = transaction.paymentInfo as OneClickPaymentInfo;
    await request.rpc("network/oneclickBridgeConfirm", {
      walletAddress: paymentInfo._wallet,
      oneclickBridgeId: paymentInfo.oneclickBridgeId,
      depositChainId: paymentInfo.token.chainId,
      depositTransactionHash: transaction.transactionHash,
    });
    this.updateTransactionStatus(
      transaction.transactionHash,
      BridgeTransactionStatus.MINTING
    );
  }

  async getOneClickBridgeUnConfirmedTasks(
    wallet: string
  ): Promise<BridgeTask[]> {
    const resp = await request.rpc(
      "network/getOneclickBridgeUnConfirmedTasks",
      {
        walletAddress: wallet,
        actionMethod: this.serviceId,
      }
    );
    return resp;
  }

  async waitUntilMintSuccess(
    transaction: BridgeTransaction,
    timeout: number = 60000
  ) {
    let endDate = Date.now() + timeout;
    const that = this;
    const res: boolean = await new Promise((resolve, reject) => {
      async function checker() {
        if (Date.now() > endDate) {
          return resolve(false);
        }
        try {
          const tasks = await that.getOneClickBridgeUnConfirmedTasks(
            transaction.paymentInfo._wallet
          );
          const isExist = tasks.find((task) => {
            return task.transactionHash === transaction.transactionHash;
          });
          if (!isExist) {
            that.updateTransactionStatus(
              transaction.transactionHash,
              BridgeTransactionStatus.MINT_SUCCESS
            );
            return resolve(true);
          }
        } catch (e) {}
        setTimeout(checker, 1000);
      }
      checker();
    });
    return res;
  }

  async deposit(paymentInfo: OneClickPaymentInfo) {
    mixpanel.track("bridge_deposit", {
      serviceId: this.serviceId,
      type: this.type,
      paymentInfo,
    });
    const { chain } = getNetwork();
    if (chain?.id !== paymentInfo.token.chainId) {
      await switchNetwork({
        chainId: paymentInfo.token.chainId,
      });
    }
    const isTokenNative = isNativeToken(paymentInfo.token.tokenAddress);
    if (!isTokenNative) {
      const resp = await writeContract({
        address: paymentInfo.token.tokenAddress as any,
        chainId: paymentInfo.token.chainId,
        abi: erc20ABI,
        functionName: "approve",
        args: [
          paymentInfo.escrowContractAddress as any,
          parseEther(paymentInfo.tokenAmount.toFixed(18)),
        ],
      });
      try {
        await waitForTransaction({
          hash: resp.hash,
          confirmations: 2,
        });
      } catch (e: any) {
        if (!(await isSkipError(e))) {
          throw e;
        }
      }
    }
    let resp;
    if (isTokenNative) {
      resp = await writeContract({
        address: paymentInfo.escrowContractAddress as any,
        chainId: paymentInfo.token.chainId,
        abi: Escrow.abi,
        functionName: "depositNativeToken",
        value: parseEther(paymentInfo.tokenAmount.toFixed(18)),
      });
    } else {
      resp = await writeContract({
        address: paymentInfo.escrowContractAddress as any,
        chainId: paymentInfo.token.chainId,
        abi: Escrow.abi,
        functionName: "depositERC20",
        args: [
          paymentInfo.token.tokenAddress,
          parseUnits(String(paymentInfo.tokenAmount), 18),
        ],
      });
    }
    this.newTransaction(resp.hash, paymentInfo);
    try {
      await waitForTransaction({
        hash: resp.hash,
        confirmations: 2,
      });
    } catch (e: any) {
      if (!(await isSkipError(e))) {
        throw e;
      }
    }

    this.updateTransactionStatus(
      resp.hash,
      BridgeTransactionStatus.DEPOSIT_SUCCESS
    );
    return this.getTransaction(resp.hash)!;
  }
}

const bridges = {
  registerVC: new OneClickBridge("vc/registerVC"),
  zkSync: new ZKSyncBridge(),
  revokeVC: new OneClickBridge("vc/removeVC"),
};

export default bridges;
